/**
 * **预算调整历史**（2026-10-04 第十二批 · 用户第 8 条；2026-10-05 第十六批 · 用户第 4 条合并两源）。
 *
 * 用户原话：「调整预算的时候，窗口显示最近 10 次预算调整前后的金额和时间。
 * （每天只记录最后一次调整）」；第十六批追问：「只能显示通过这个软件修改的吗？
 * 能不能显示所有渠道修改的记录？」
 *
 * ## 两个来源（后端合并，前端只展示）
 *
 *  · **本系统** = `operation_log`（权威、带 run 关联、有成功/失败）；
 *  · **Meta 审计** = 账户级 `GET /act_x/activities`（**所有渠道**：后台手动、Power Editor、
 *    自动规则、其它工具）——后端**按需拉取**（本组件只在弹窗打开时挂载）、有 TTL 缓存与
 *    翻页上限，并按「对象 + 时间接近 + 前后值一致」把我们自己的写入回声**去重**。
 *
 * ## 三条硬约束
 *
 *  · `beforeBudgetMinorUnits === null` → 「—（无前值）」，**绝不写成 0 / 绝不编造**；
 *  · **每天只保留最后一次**在后端做完（前端不另做一套去重，避免两套口径分叉）；
 *  · Meta 审计缺失/权限不足/翻页未覆盖时，`metaAudit.note` **如实显示**「仅本系统记录 /
 *    可能未覆盖完整」——**绝不**编造「没有其它渠道改动」（口径红线）。
 */

import { useEffect, useState, type ReactElement } from "react";
import { apiFetchBudgetHistory } from "../lib/apiClient.js";
import type { AdEntityLevel, BudgetHistoryEntryRow, BudgetHistoryMetaAudit } from "../lib/apiTypes.js";
import { DASH, formatCents, formatCentsOrDash, formatInt } from "../lib/format.js";

export interface BudgetHistoryListProps {
  readonly accountId: string;
  readonly level: AdEntityLevel;
  readonly entityId: string;
  /** 可选：按父实体汇总（ABO 批量弹窗）。 */
  readonly parentEntityId?: string;
  /** 金额展示币种（与弹窗一致；同一实体同账户 → 同币种）。 */
  readonly currency: string;
  /** 条数上限；默认 10（用户口径）。 */
  readonly limit?: number;
  /**
   * 每次外部写入成功后 +1 → 重新拉取（例如确认真写之后）。
   * 不传 = 只在打开 / 切换实体时拉一次。
   */
  readonly refreshToken?: number;
  /** 标题文案（默认「最近 N 次预算调整」）。 */
  readonly title?: string;
}

/**
 * `occurredAtIso` → 账户时区的 `YYYY-MM-DD HH:mm`（可复核，不用浏览器本地时区顶替）。
 *
 * 用 `sv-SE` 的固定格式（`2026-10-04 21:38`）而不是 `toLocaleString`：
 * 后者在不同宿主 / 语言下输出不同，测试与截图无法对齐。
 */
export function formatBudgetHistoryTime(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    const parts = new Intl.DateTimeFormat("sv-SE", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
    return parts;
  } catch {
    // 非法时区 → 退回 UTC（绝不抛错、也绝不假装是账户时区）。
    return new Intl.DateTimeFormat("sv-SE", {
      timeZone: "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  }
}

const RESULT_LABEL: Readonly<Record<BudgetHistoryEntryRow["result"], string>> = {
  succeeded: "成功",
  failed: "失败",
  attempted: "已提交（无结果）",
  meta_audit: "Meta 审计",
};

/** 来源标签（Meta 审计 = 其它渠道；本系统 = operation_log）。 */
const SOURCE_LABEL: Readonly<Record<"system" | "meta", string>> = {
  system: "本系统",
  meta: "Meta 审计",
};

/**
 * Meta 审计侧的一句话状态（纯函数、可单测）。
 *
 * `null` = 本次没请求 Meta（老后端 / 未合并）→ 不显示；`status="ok"` 且未截断 → 说明覆盖面；
 * 其余（不可用 / 无令牌 / 报错 / 截断）→ **如实**给出「仅本系统记录」之类的说明。
 */
export function metaAuditNote(audit: BudgetHistoryMetaAudit | undefined): string | null {
  if (audit === undefined || audit.requested === false) return null;
  const scanned = `扫描 ${formatInt(audit.eventsScanned)} 条事件 / 命中 ${formatInt(audit.metaEventsConsidered)} 条预算改动`;
  if (audit.status === "ok" && !audit.truncated) {
    return `已合并 Meta 审计（所有渠道：${scanned}；与本系统重复 ${formatInt(audit.duplicatesDropped)} 条已去重）`;
  }
  if (audit.status === "ok" && audit.truncated) {
    // 拉到了一些、但翻页到上限：**不能**说「仅本系统」，也不能说「没有其它渠道改动」。
    return `Meta 审计可能未覆盖完整（${scanned}；${audit.note ?? "翻页到上限"}）`;
  }
  const reason =
    audit.note ??
    (audit.status === "error"
      ? "Meta 审计拉取失败"
      : audit.status === "no_token"
        ? "未配置 Meta 读取执行器"
        : audit.status === "unavailable"
          ? "后端未提供对象/时区上下文"
          : "Meta 审计不可用");
  return `仅本系统记录：${reason}`;
}

/**
 * 「调整前」的展示文本（纯函数、可单测）。
 *
 * `null` = **这条历史没有前值**（老记录 / 当时无预算基准）→ 「—（无前值）」，
 * **绝不折成 0、绝不编造**（口径红线）。非 null 才按币种格式化。
 */
export function budgetHistoryBeforeText(before: number | null, currency: string): string {
  return before === null ? `${DASH}（无前值）` : formatCentsOrDash(before, currency);
}

/**
 * 「操作人」的展示文本（纯函数、可单测）。
 *
 * ⭐ 2026-10-13 用户要求：「修改记录要增加操作人（可能是人，也可能是系统）」。
 * 后端已归一：**人工 = 用户名**；**系统 = `系统·<机制>`**（例如 `系统·目标爬坡`）。
 * 优先级：归一后的 `actor` > Meta 原始 `actorName`（Meta 审计条目只有后者）。
 * ⚠️ 两者都没有 ⇒ **`—`** —— 用户要求显式（⛔ 留空会被当成"根本没有这一栏"）。
 */
export function budgetHistoryActorText(item: {
  readonly actor?: string | null;
  readonly actorName?: string | null;
}): string {
  const text = item.actor ?? item.actorName ?? null;
  return text === null || text === "" ? DASH : text;
}

/**
 * 叠加层的展示文本（纯函数、可单测）—— ⭐ 用户 2026-10-14 要求「**列内标注来源**」。
 *
 * 背景（用户原话）：「爬坡仅仅是作用在自动预算上边的调整，**它自己不会操作调整**」
 * ⇒ 一次下发只有**一条**预算审计（`operation_log`），里面带**两个来源**：
 *    基准 = 自动预算 · 叠加 = 目标爬坡。
 * ⇒ 面板必须把"这一笔里有多少是爬坡叠加"**连来源一起**显示 ⇒ ⛔ 绝不允许一个无来源的数字。
 *
 * 四种形态（⛔ **缺失 ≠ 0** —— 这是本函数唯一的判断纪律）：
 *  · `overlayCents` 是数字（含 **0**：爬坡算了但没加）⇒ 有叠加 → `+ 爬坡叠加 $12.34`；
 *  · 字段在、但值是 `null` / 非数字（后端明说"这笔没有叠加"）⇒ 「无叠加」，⛔ **不是 $0.00**；
 *  · 键**整个缺失**（老记录 / 老后端 / Meta 审计条目）⇒ 返回 `null` ⇒ 调用方显示「—」
 *    （"没记到"与"没有叠加"是两件事，⛔ 不许折成同一个）；
 *  · `title` = 悬停解释（回答"这个数是谁给的、为什么没有"）。
 *
 * 前端**不做任何算术**：金额直接取后端字段（**生效**叠加量），按该行币种格式化
 * —— 与「调整前 / 调整后」用同一把尺子（`formatCents`）。
 */
export function budgetHistoryOverlayText(
  item: {
    readonly overlayCents?: number | null;
    readonly overlaySource?: string | null;
    readonly currency?: string | null;
  },
  currency: string,
): { readonly has: boolean; readonly text: string; readonly title: string } | null {
  const cents = item.overlayCents;
  const rowCurrency = item.currency ?? currency;
  if (typeof cents === "number" && Number.isFinite(cents)) {
    const sourceNote =
      item.overlaySource === null || item.overlaySource === undefined ? "" : `；叠加来源：${item.overlaySource}`;
    return {
      has: true,
      text: `+ 爬坡叠加 ${formatCents(cents, rowCurrency)}`,
      title: `这一笔里由【目标爬坡】叠加的部分（后端给的**生效量**）${sourceNote}；前端不做算术。`,
    };
  }
  if (cents === undefined && item.overlaySource === undefined) return null; // 键缺失 ⇒ 调用方显示「—」
  return {
    has: false,
    text: "无叠加",
    title:
      "这一笔没有爬坡叠加（老记录 / 本层本轮没参与爬坡）。" +
      "「没有叠加」≠「叠加了 0 元」，所以这里**不写 $0.00**。",
  };
}

export function BudgetHistoryList({
  accountId,
  level,
  entityId,
  parentEntityId,
  currency,
  limit = 10,
  refreshToken = 0,
  title,
}: BudgetHistoryListProps): ReactElement {
  const [items, setItems] = useState<readonly BudgetHistoryEntryRow[]>([]);
  const [metaAudit, setMetaAudit] = useState<BudgetHistoryMetaAudit | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    apiFetchBudgetHistory(
      {
        accountId,
        level,
        entityId,
        ...(parentEntityId === undefined ? {} : { parentEntityId }),
        limit,
        // 第十六批：打开弹窗（本组件挂载）时才**按需**拉 Meta 审计（后端有 5 分钟缓存）。
        includeMeta: true,
      },
      controller.signal,
    )
      .then((data) => {
        if (controller.signal.aborted) return;
        setItems(data.items);
        setMetaAudit(data.metaAudit);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : String(cause));
        setItems([]);
        setMetaAudit(undefined);
        setLoading(false);
      });
    return () => controller.abort();
  }, [accountId, level, entityId, parentEntityId, limit, refreshToken]);

  const auditNote = metaAuditNote(metaAudit);

  return (
    <section className="rounded-control border border-line-subtle" data-testid="budget-history">
      <header className="flex items-center justify-between border-b border-line-subtle bg-sunken px-2 py-1">
        <h3 className="text-meta font-medium text-ink">
          {title ?? `最近 ${formatInt(limit)} 次预算调整`}
        </h3>
        <span className="text-2xs text-ink-3">
          本系统 + Meta 审计（所有渠道）；按账户时区每天只记最后一次；dry-run 不计
        </span>
      </header>

      {loading ? <p className="px-2 py-2 text-meta text-ink-3">正在读取预算调整历史…</p> : null}
      {error === null ? null : (
        <p className="px-2 py-2 text-meta text-danger-text">预算调整历史读取失败：{error}</p>
      )}
      {/* Meta 审计侧的**如实**状态：不可用 / 无令牌 / 报错 / 截断都不许沉默。 */}
      {loading || auditNote === null ? null : (
        <p
          className={`border-b border-line-subtle px-2 py-1 text-2xs ${
            metaAudit?.status === "ok" && metaAudit.truncated !== true ? "text-ink-3" : "text-warning-text"
          }`}
          data-testid="budget-history-meta-audit"
          data-meta-audit-status={metaAudit?.status ?? "unknown"}
        >
          {auditNote}
        </p>
      )}
      {!loading && error === null && items.length === 0 ? (
        <p className="px-2 py-2 text-meta text-ink-3" data-testid="budget-history-empty">
          暂无预算调整记录（只统计真实写入；dry-run 不计）。
          {/* ⭐ 2026-10-14 用户裁定：爬坡不再是独立写者 ⇒ 空态必须**解释去哪儿看**，
              否则用户会以为"爬坡不生效了"（它的叠加量现在长在预算调整记录里）。 */}
          <span className="mt-1 block text-2xs">
            爬坡（目标爬坡）不再是独立写者：它算出的叠加量会显示在这里的对应记录里，并标注来源
            （「+ 爬坡叠加 …」）；自动预算页面上的「最近执行」表只列爬坡自己发起并已生效的历史。
          </span>
        </p>
      ) : null}

      {items.length === 0 ? null : (
        <div className="max-h-[28vh] overflow-auto">
          {/*
            ⭐ 2026-10-14（用户要求"列内标注来源"）：表头附近给一句人话说明 ——
            否则「+ 爬坡叠加 …」会被读成"另一条记录"，而不是"这一笔里的一个来源"。
          */}
          <p className="border-b border-line-subtle px-2 py-1 text-2xs text-ink-3" data-testid="budget-history-source-note">
            来源标注在「来源」列内：`系统·自动预算` = 这一笔的基准；`+ 爬坡叠加 …` = 同一条记录里
            【目标爬坡】贡献的部分（取后端给的生效量）。爬坡不再是独立写者 ⇒ 它不会在这里多出一条单独记录。
          </p>
          <table className="w-full border-collapse text-table">
            <thead className="sticky top-0 bg-surface text-left text-meta text-ink-3">
              <tr className="border-b border-line-subtle">
                <th className="px-2 py-1 font-medium">时间</th>
                <th className="px-2 py-1 font-medium">来源</th>
                <th className="px-2 py-1 text-right font-medium">调整前</th>
                <th className="px-2 py-1 text-right font-medium">调整后</th>
                <th className="px-2 py-1 text-right font-medium">结果</th>
              </tr>
            </thead>
            <tbody className="tnum">
              {items.map((item) => {
                const source = item.source ?? "system";
                // ⭐ 2026-10-13：操作人文案走**纯函数**（唯一来源、可单测）。
                //    后端归一：人工 = 用户名；系统 = `系统·<机制>`；都没有 ⇒ `—`。
                const actorText = budgetHistoryActorText(item);
                // ⭐ 2026-10-14：叠加量 + 来源标注走**纯函数**（缺失 ⇒ `null` ⇒ 显示「—」）。
                const overlay = budgetHistoryOverlayText(item, item.currency ?? currency);
                return (
                  <tr key={item.logId} className="border-b border-line-subtle">
                    <td className="px-2 py-1 text-ink-2" title={`账户时区 ${item.timezone} · 日志 ${item.logId}`}>
                      {formatBudgetHistoryTime(item.occurredAtIso, item.timezone)}
                    </td>
                    <td className="px-2 py-1 text-2xs" data-budget-source={source}>
                      <span className={source === "meta" ? "text-ink-2" : "text-ink-3"}>{SOURCE_LABEL[source]}</span>
                      {/* 操作人：能拿到就显示（人 / 系统·机制）；拿不到 ⇒ 「—」（**不编造**、也不留空）。 */}
                      <span className="ml-1 text-ink-3" data-budget-actor={actorText}>
                        · {actorText}
                      </span>
                      {overlay === null ? (
                        // 键缺失（老记录 / Meta 审计条目）⇒ 「—」：⛔ 不假装"没有叠加"、⛔ 更不写 0。
                        <span className="ml-1 text-ink-3" data-budget-overlay="missing" title="这条记录没带叠加信息（老记录或来自 Meta 审计）——如实显示，不编造。">
                          · {DASH}
                        </span>
                      ) : (
                        <span
                          className={`ml-1 ${overlay.has ? "text-ink-2" : "text-ink-3"}`}
                          data-budget-overlay={overlay.has ? "overlay" : "none"}
                          {...(overlay.has ? { "data-budget-overlay-cents": String(item.overlayCents) } : {})}
                          title={overlay.title}
                        >
                          · {overlay.text}
                        </span>
                      )}
                      {item.applicationName == null ? null : (
                        <span className="ml-1 text-ink-3">（{item.applicationName}）</span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right text-ink-2" data-budget-before={item.beforeBudgetMinorUnits ?? "null"}>
                      {item.beforeBudgetMinorUnits === null ? (
                        <span className="text-ink-3" title="这条历史里没有前值（老记录或提交时无预算基准）——如实显示，不编造">
                          {budgetHistoryBeforeText(null, item.currency ?? currency)}
                        </span>
                      ) : (
                        budgetHistoryBeforeText(item.beforeBudgetMinorUnits, item.currency ?? currency)
                      )}
                    </td>
                    <td className="px-2 py-1 text-right text-ink">
                      {formatCentsOrDash(item.afterBudgetMinorUnits, item.currency ?? currency)}
                    </td>
                    <td className="px-2 py-1 text-right text-2xs">
                      <span
                        className={
                          item.result === "succeeded"
                            ? "text-success-text"
                            : item.result === "failed"
                              ? "text-danger-text"
                              : "text-ink-3"
                        }
                      >
                        {RESULT_LABEL[item.result]}
                      </span>
                      {item.errorDetail === null ? null : (
                        <span className="ml-1 text-ink-3" title={item.errorCode ?? undefined}>
                          · {item.errorDetail}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
